import { useEffect, useRef, useState } from 'react';
import { ChevronDownIcon, DocumentTextIcon } from '@heroicons/react/24/outline';
import { PDFViewer } from '@odo/shared-ui';
import api from '../../../../api/config';
import { fetchCloudFileDownloadUrl } from '../../../../services/patientFiles';
import { apiErrorMessage } from '../../../../utils/apiError';
import { DocumentPanelPlaceholder } from './DocumentPanelPlaceholder';
import { TIMELINE_VERSION_FETCH_LIMIT } from './timelineVersionUtils';

// Shared shape for a versioned clinical document (Dx report / SDI / RDI).
export interface DocumentVersion {
    id: string;
    version: number;
    createdAt: string;
    patientFileId: string;
    fileName: string;
}

/** Fetch state of the `versions` list itself — distinct from a single
 *  version's PDF download-url fetch, which VersionedPdfPane tracks on its own. */
export type VersionsFetchState = 'loading' | 'error' | 'ready';

interface VersionedPdfPaneProps {
    patientId: string | null;
    /** Timeline document versions, latest first. */
    versions: DocumentVersion[];
    docLabel?: string;
    versionsState: VersionsFetchState;
    onRetryVersions?: () => void;
    /** True when the version fetch was truncated - older versions may be hidden. */
    hasMoreVersions?: boolean;
}

interface VersionDropdownTriggerProps {
    current: DocumentVersion;
    open: boolean;
    onToggle: () => void;
}

function VersionDropdownTrigger({ current, open, onToggle }: VersionDropdownTriggerProps) {
    return (
        <button
            type="button"
            onClick={onToggle}
            aria-haspopup="menu"
            aria-expanded={open}
            className="inline-flex items-center gap-2 px-3 py-1.5 rounded-lg border border-gray-200 bg-white hover:bg-gray-50 hover:border-gray-300 transition-colors text-sm font-medium text-gray-700 min-w-0 max-w-full"
        >
            <span className="text-[10px] font-semibold text-gray-600 bg-gray-100 px-1.5 py-0.5 rounded flex-shrink-0">
                v{current.version}
            </span>
            <span className="truncate min-w-0 flex-1">{current.fileName}</span>
            <ChevronDownIcon
                className={
                    'w-4 h-4 flex-shrink-0 text-gray-400 transition-transform ' +
                    (open ? 'rotate-180' : '')
                }
            />
        </button>
    );
}

interface VersionDropdownMenuProps {
    versions: DocumentVersion[];
    currentId: string;
    onSelect: (id: string) => void;
}

function VersionDropdownMenu({ versions, currentId, onSelect }: VersionDropdownMenuProps) {
    return (
        <div
            role="menu"
            className="absolute left-0 top-full mt-1 z-30 w-[360px] bg-white border border-gray-200 rounded-lg shadow-lg overflow-hidden"
        >
            {versions.map((v) => {
                const isActive = v.id === currentId;
                return (
                    <button
                        key={v.id}
                        type="button"
                        role="menuitem"
                        onClick={() => onSelect(v.id)}
                        className={
                            'w-full flex items-center gap-2 px-3 py-2.5 text-left transition-colors border-b border-gray-100 last:border-b-0 ' +
                            (isActive ? 'bg-gray-50' : 'hover:bg-gray-50')
                        }
                    >
                        <span className="text-[10px] font-semibold text-gray-600 bg-gray-100 px-1.5 py-0.5 rounded flex-shrink-0">
                            v{v.version}
                        </span>
                        <div className="flex-1 min-w-0">
                            <div className="text-sm font-medium text-gray-800 truncate">
                                {v.fileName}
                            </div>
                            <div className="text-[11px] text-gray-500">
                                {v.createdAt
                                    ? new Date(v.createdAt).toLocaleDateString('en-US', {
                                          month: 'short',
                                          day: 'numeric',
                                          year: 'numeric'
                                      })
                                    : ''}
                            </div>
                        </div>
                    </button>
                );
            })}
        </div>
    );
}

interface VersionDropdownProps {
    versions: DocumentVersion[];
    selectedId: string | null;
    onSelect: (id: string) => void;
}

function VersionDropdown({ versions, selectedId, onSelect }: VersionDropdownProps) {
    const [open, setOpen] = useState(false);
    const wrapperRef = useRef<HTMLDivElement>(null);

    useEffect(() => {
        if (!open) return;
        const handleClick = (e: MouseEvent) => {
            if (wrapperRef.current && !wrapperRef.current.contains(e.target as Node)) {
                setOpen(false);
            }
        };
        const handleKey = (e: KeyboardEvent) => {
            if (e.key === 'Escape') setOpen(false);
        };
        document.addEventListener('mousedown', handleClick);
        document.addEventListener('keydown', handleKey);
        return () => {
            document.removeEventListener('mousedown', handleClick);
            document.removeEventListener('keydown', handleKey);
        };
    }, [open]);

    // versions[0] fallback is only safe once we've confirmed the list isn't
    // empty - don't lean on the caller's length gate for that guarantee.
    const current = versions.find((v) => v.id === selectedId) ?? versions[0];
    if (!current) return null;

    return (
        <div ref={wrapperRef} className="relative min-w-0 max-w-[320px]">
            <VersionDropdownTrigger
                current={current}
                open={open}
                onToggle={() => setOpen((o) => !o)}
            />
            {open && (
                <VersionDropdownMenu
                    versions={versions}
                    currentId={current.id}
                    onSelect={(id) => {
                        setOpen(false);
                        onSelect(id);
                    }}
                />
            )}
        </div>
    );
}

function useVersionSelection(versions: DocumentVersion[], patientId: string | null) {
    const [selectedId, setSelectedId] = useState<string | null>(null);
    const userPickedRef = useRef(false);

    useEffect(() => {
        userPickedRef.current = false;
    }, [patientId]);

    useEffect(() => {
        if (versions.length === 0) {
            setSelectedId(null);
            return;
        }
        setSelectedId((prev) => {
            if (userPickedRef.current && prev && versions.some((v) => v.id === prev)) {
                return prev;
            }
            return versions[0].id;
        });
    }, [versions]);

    const selectVersion = (id: string): void => {
        userPickedRef.current = true;
        setSelectedId(id);
    };

    const selectedVersion = versions.find((v) => v.id === selectedId) ?? null;
    return { selectedId, selectedVersion, selectVersion };
}

function useVersionDownloadUrl(patientId: string | null, selectedVersion: DocumentVersion | null) {
    const [versionUrl, setVersionUrl] = useState<string | null>(null);
    const [versionError, setVersionError] = useState<string | null>(null);

    useEffect(() => {
        if (!selectedVersion || !patientId) {
            setVersionUrl(null);
            setVersionError(null);
            return;
        }
        let cancelled = false;
        setVersionUrl(null);
        setVersionError(null);
        fetchCloudFileDownloadUrl(patientId, selectedVersion.patientFileId)
            .then((url) => {
                if (!cancelled) setVersionUrl(url);
            })
            .catch((err: unknown) => {
                if (cancelled) return;
                setVersionError(apiErrorMessage(err, 'Failed to load this version.'));
            });
        return () => {
            cancelled = true;
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [selectedVersion?.patientFileId, patientId]);

    return { versionUrl, versionError };
}

interface VersionLeadingControlsProps {
    versions: DocumentVersion[];
    selectedId: string | null;
    selectedVersion: DocumentVersion | null;
    hasMoreVersions: boolean;
    onSelect: (id: string) => void;
}

function VersionLeadingControls({
    versions,
    selectedId,
    selectedVersion,
    hasMoreVersions,
    onSelect
}: VersionLeadingControlsProps) {
    const picker =
        versions.length <= 1 ? (
            selectedVersion && (
                <div className="flex items-center gap-2 text-sm font-medium text-gray-700 min-w-0 max-w-[320px]">
                    <span className="text-[10px] font-semibold text-gray-600 bg-gray-100 px-1.5 py-0.5 rounded flex-shrink-0">
                        v{selectedVersion.version}
                    </span>
                    <span className="truncate min-w-0 flex-1">{selectedVersion.fileName}</span>
                </div>
            )
        ) : (
            <VersionDropdown versions={versions} selectedId={selectedId} onSelect={onSelect} />
        );

    if (!picker) return null;
    return (
        <div className="flex items-center gap-2 min-w-0">
            {picker}
            {hasMoreVersions && (
                <span className="text-[10px] text-gray-400 truncate min-w-0 flex-shrink">
                    Showing latest {TIMELINE_VERSION_FETCH_LIMIT}
                </span>
            )}
        </div>
    );
}

interface PdfPaneFallbackProps {
    versionsState: VersionsFetchState;
    versionUrl: string | null;
    versionError: string | null;
    versionsCount: number;
    docLabel: string;
    onRetryVersions?: () => void;
}

// A versions-fetch failure must never fall through to "no document attached" - that hides the error from the approve/decline reviewer.
function PdfPaneFallback({
    versionsState,
    versionUrl,
    versionError,
    versionsCount,
    docLabel,
    onRetryVersions
}: PdfPaneFallbackProps) {
    if (versionsState === 'error') {
        return (
            <DocumentPanelPlaceholder
                icon={DocumentTextIcon}
                tone="error"
                message={`Failed to load ${docLabel} versions. Please retry before deciding.`}
                onRetry={onRetryVersions}
            />
        );
    }

    if (versionsState === 'loading') {
        return (
            <DocumentPanelPlaceholder icon={DocumentTextIcon} message={`Loading ${docLabel}...`} />
        );
    }

    // Ready but no PDF resolved yet — a per-version download-url error, or
    // nothing selected because there's nothing to select.
    if (versionUrl) return null;
    if (versionError) {
        return (
            <DocumentPanelPlaceholder icon={DocumentTextIcon} tone="error" message={versionError} />
        );
    }
    return (
        <DocumentPanelPlaceholder
            icon={DocumentTextIcon}
            message={
                versionsCount > 0
                    ? `Loading ${docLabel}...`
                    : `No ${docLabel} attached to this case yet.`
            }
        />
    );
}

// Left pane shared by every clinical-document review task (Dx report / SDI / RDI): the PDF with a version dropdown.
export function VersionedPdfPane({
    patientId,
    versions,
    docLabel = 'document',
    versionsState,
    onRetryVersions,
    hasMoreVersions = false
}: VersionedPdfPaneProps) {
    const { selectedId, selectedVersion, selectVersion } = useVersionSelection(versions, patientId);
    const { versionUrl, versionError } = useVersionDownloadUrl(patientId, selectedVersion);

    if (versionsState !== 'ready' || !versionUrl) {
        return (
            <PdfPaneFallback
                versionsState={versionsState}
                versionUrl={versionUrl}
                versionError={versionError}
                versionsCount={versions.length}
                docLabel={docLabel}
                onRetryVersions={onRetryVersions}
            />
        );
    }

    return (
        <div className="h-full bg-white border border-gray-200 rounded-xl overflow-hidden p-3">
            <PDFViewer
                pdfUrl={versionUrl}
                apiBaseUrl={api.defaults.baseURL ?? ''}
                showControls
                className="h-full"
                getAuthToken={() => localStorage.getItem('odo_login_token')}
                leadingControls={
                    <VersionLeadingControls
                        versions={versions}
                        selectedId={selectedId}
                        selectedVersion={selectedVersion}
                        hasMoreVersions={hasMoreVersions}
                        onSelect={selectVersion}
                    />
                }
            />
        </div>
    );
}
